// =============================================================================
// screens/LeaderDashboardScreen.tsx — the CRYB Leader control center.
//
// Shown in the drawer only when User.isLeader. Surfaces a leader's communities
// with quick access to the creator/admin tools that already exist on
// CommunityScreen (edit banner/picture, channels, moderation) plus a Create CTA.
// Stage 2 of the leader flow; deeper per-community analytics/roles land next.
// =============================================================================
import { useCallback, useState, useLayoutEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, ActivityIndicator, TouchableOpacity, RefreshControl } from 'react-native';
import CachedImage from '../components/ui/CachedImage';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Ionicons } from '../components/ui/Icon';
import { LEADER_ONBOARDING_SEEN_KEY } from './LeaderOnboardingScreen';
import ParticleBackground from '../components/ui/ParticleBackground';
import Asset3D from '../components/ui/Asset3D';
import { GlassCard, GlassButton, GlassSurface } from '../components/glass';
import { colors, spacing, radius, fonts, typographyScale } from '../constants/theme';
import { api } from '../services/api';
import { communityIcon } from '../lib/icons';
import type { RootStackParamList } from '../navigation/types';

type Nav = NativeStackNavigationProp<RootStackParamList, 'LeaderDashboard'>;

export default function LeaderDashboardScreen() {
  const navigation = useNavigation<Nav>();
  const insets = useSafeAreaInsets();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [communities, setCommunities] = useState<any[]>([]);

  useLayoutEffect(() => { navigation.setOptions({ headerShown: false }); }, [navigation]);

  const load = useCallback(async () => {
    try {
      const res = await api<any>('/users/me/communities');
      const d: any = res?.data ?? {};
      const list = d.items ?? d.communities ?? (Array.isArray(d) ? d : []);
      // Only overwrite on a successful, well-formed response — a failed refetch
      // must NOT wipe already-loaded communities to an empty "no communities" state.
      if (res?.success && Array.isArray(list)) setCommunities(list);
    } catch {
      /* keep whatever we already have; finally clears the spinner */
    } finally {
      setLoading(false);
    }
  }, []);
  useFocusEffect(useCallback(() => { load(); }, [load]));

  // Stage 3: one-time leader onboarding on first dashboard open (post-approval).
  useFocusEffect(useCallback(() => {
    let cancelled = false;
    (async () => {
      try {
        const seen = await AsyncStorage.getItem(LEADER_ONBOARDING_SEEN_KEY);
        if (!seen && !cancelled) navigation.navigate('LeaderOnboarding');
      } catch { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [navigation]));

  const onRefresh = useCallback(async () => { setRefreshing(true); await load(); setRefreshing(false); }, [load]);

  const Header = (
    <GlassSurface variant="bar" radius={0} sheen={false} surfaceColor="transparent"
      style={[styles.header, { paddingTop: insets.top }]} contentStyle={styles.headerContent}>
      <TouchableOpacity onPress={() => navigation.goBack()} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }} accessibilityRole="button" accessibilityLabel="Back">
        <Ionicons name="chevron-back" size={26} color={colors.ink} />
      </TouchableOpacity>
      <Text style={styles.headerTitle}>Leader Dashboard</Text>
      <View style={{ width: 26 }} />
    </GlassSurface>
  );

  return (
    <View style={styles.screen}>
      <ParticleBackground theme="light" />
      {Header}
      <ScrollView
        contentContainerStyle={[styles.content, { paddingBottom: insets.bottom + spacing.xxxl }]}
        showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={colors.primary} />}
      >
        <View style={styles.hero}>
          <Asset3D name="medal" size={56} />
          <Text style={styles.heroTitle}>Your communities</Text>
          <Text style={styles.heroSub}>Create, customize and moderate the spaces you lead.</Text>
        </View>

        <GlassButton
          title="Create a community"
          onPress={() => navigation.navigate('CreateCommunity')}
          variant="primary"
          style={{ marginBottom: spacing.lg }}
        />

        {loading ? (
          <View style={styles.center}><ActivityIndicator size="large" color={colors.primary} /></View>
        ) : communities.length === 0 ? (
          <GlassCard style={styles.emptyCard} padding={spacing.lg}>
            <Text style={styles.emptyTitle}>No communities yet</Text>
            <Text style={styles.emptySub}>Create your first community to unlock the leader tools.</Text>
          </GlassCard>
        ) : (
          communities.map((c: any, i: number) => {
            const name = c.name || c.slug;
            const title = c.displayName || c.name || 'Community';
            const img = communityIcon(c.name, c.displayName, c.icon || c.avatar);
            const members = c.memberCount ?? c.members ?? null;
            return (
              <GlassCard key={c.id || name || i} style={styles.commCard} contentStyle={styles.commRow} padding={spacing.md}
                onPress={() => name && navigation.navigate('CommunityManage', { name })}>
                {img ? <CachedImage source={{ uri: img }} style={styles.commIcon} /> : <View style={[styles.commIcon, styles.commIconFallback]}><Text style={styles.commIconLetter}>{title.charAt(0).toUpperCase()}</Text></View>}
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text style={styles.commName} numberOfLines={1}>{title}</Text>
                  <Text style={styles.commMeta} numberOfLines={1}>c/{name}{members != null ? ` · ${Number(members).toLocaleString()} members` : ''}</Text>
                </View>
                <View style={styles.manageChip}>
                  <Ionicons name="settings-outline" size={14} color={colors.primary} />
                  <Text style={styles.manageText}>Manage</Text>
                </View>
              </GlassCard>
            );
          })
        )}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, backgroundColor: colors.bg },
  center: { paddingVertical: spacing.xxxl, alignItems: 'center' },
  header: { position: 'absolute', top: 0, left: 0, right: 0, zIndex: 10, borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: colors.cardBorder },
  headerContent: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: spacing.md, paddingBottom: spacing.sm },
  headerTitle: { ...typographyScale.h3, color: colors.ink, ...fonts.semibold },
  content: { paddingHorizontal: spacing.xl, paddingTop: 100 },
  hero: { alignItems: 'center', marginBottom: spacing.lg },
  heroTitle: { ...typographyScale.h2, color: colors.ink, ...fonts.bold, marginTop: spacing.sm },
  heroSub: { ...typographyScale.body, color: colors.muted, textAlign: 'center', marginTop: spacing.xs },
  emptyCard: { alignItems: 'center' },
  emptyTitle: { ...typographyScale.h3, color: colors.ink, ...fonts.bold },
  emptySub: { ...typographyScale.body, color: colors.muted, textAlign: 'center', marginTop: spacing.xs },
  commCard: { marginBottom: spacing.md },
  commRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.md },
  commIcon: { width: 44, height: 44, borderRadius: 22, backgroundColor: colors.primary + '10' },
  commIconFallback: { alignItems: 'center', justifyContent: 'center' },
  commIconLetter: { ...fonts.extrabold, fontSize: 18, color: colors.primary },
  commName: { ...fonts.semibold, fontSize: 15, color: colors.ink },
  commMeta: { ...fonts.regular, fontSize: 12, color: colors.muted, marginTop: 2 },
  manageChip: { flexDirection: 'row', alignItems: 'center', gap: 4, backgroundColor: colors.primary + '12', borderRadius: radius.full, paddingHorizontal: spacing.sm, paddingVertical: 5 },
  manageText: { ...fonts.semibold, fontSize: 12, color: colors.primary },
});
